import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, User } from 'lucide-react';
import type { AppointmentListItem } from '@/hooks/useAppointments';

interface AgendaViewProps {
  appointments: AppointmentListItem[];
  onAppointmentClick: (id: string) => void;
}

function startOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function AgendaView({ appointments, onAppointmentClick }: AgendaViewProps) {
  const { t, i18n } = useTranslation();

  const sortedAppointments = useMemo(() => {
    return [...appointments].sort(
      (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
    );
  }, [appointments]);

  const groupedByDay = useMemo(() => {
    const groups: Array<{ date: Date; appointments: AppointmentListItem[] }> = [];
    let currentDate: Date | null = null;
    let currentGroup: AppointmentListItem[] = [];

    for (const apt of sortedAppointments) {
      const aptDate = startOfDay(new Date(apt.starts_at));

      if (currentDate === null || !isSameDay(currentDate, aptDate)) {
        if (currentDate !== null) {
          groups.push({ date: currentDate, appointments: currentGroup });
        }
        currentDate = aptDate;
        currentGroup = [apt];
      } else {
        currentGroup.push(apt);
      }
    }

    if (currentDate !== null && currentGroup.length > 0) {
      groups.push({ date: currentDate, appointments: currentGroup });
    }

    return groups;
  }, [sortedAppointments]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [i18n.language],
  );

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language],
  );

  if (appointments.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <Calendar className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {t('appointments.no_appointments')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groupedByDay.map((group) => (
        <div
          key={group.date.toISOString()}
          className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark"
        >
          {/* Day header */}
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="font-medium text-gray-900 dark:text-white">
              {dateFormatter.format(group.date)}
            </h3>
          </div>

          {/* Appointments list */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {group.appointments.map((apt) => {
              const start = new Date(apt.starts_at);
              const end = new Date(apt.ends_at);
              const serviceColor = apt.service_color || '#6366F1';
              const visitStatusColors: Record<string, string> = {
                checked_in:        '#3B82F6',
                under_treatment:   '#F97316',
                treatment_finished:'#22C55E',
                payment_completed: '#A855F7',
              };
              const color = apt.visit_status ? visitStatusColors[apt.visit_status] : serviceColor;

              return (
                <button
                  key={apt.id}
                  type="button"
                  onClick={() => onAppointmentClick(apt.id)}
                  className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  {/* Color indicator */}
                  <div
                    className="h-12 w-1 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: color }}
                  />

                  {/* Time */}
                  <div className="flex w-32 flex-shrink-0 items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Clock className="h-4 w-4" />
                    <span>
                      {timeFormatter.format(start)} – {timeFormatter.format(end)}
                    </span>
                  </div>

                  {/* Patient & Service info */}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {apt.patient_name}
                    </div>
                    {apt.service_name && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {apt.service_name}
                      </div>
                    )}
                  </div>

                  {/* Clinician */}
                  {apt.clinician_name && (
                    <div className="flex w-40 flex-shrink-0 items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <User className="h-4 w-4" />
                      <span className="truncate">{apt.clinician_name}</span>
                    </div>
                  )}

                  {/* Visit status badge */}
                  {apt.visit_status && (
                    <div
                      className="flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium text-white"
                      style={{ backgroundColor: color }}
                    >
                      {apt.visit_status === 'checked_in' && '✓ Tjekket ind'}
                      {apt.visit_status === 'under_treatment' && '⚕ Under behandling'}
                      {apt.visit_status === 'treatment_finished' && '✓ Behandling færdig'}
                      {apt.visit_status === 'payment_completed' && '✓ Betalt'}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
